import { createContext, useContext, useState, useEffect, useMemo } from "react";
import { useAuth, useToast } from "../hooks";
import { useIntegrations } from "./IntegrationsContext";
import { NOTIFICATION_TYPES } from "../utils";
import {
  connectIntegration,
  disconnectIntegration,
  getTeamName,
  AUTH_TYPES,
} from "../utils/integrationService";

const HubspotContext = createContext();

const TEAM_INTEGRATIONS_ENDPOINT = "integration/team-integrations/";

export const useHubspot = () => {
  const context = useContext(HubspotContext);
  if (!context) {
    throw new Error("useHubspot must be used within HubspotProvider");
  }
  return context;
};

export const HubspotProvider = ({ children }) => {
  const { teamId, userId, user, apiClient, getOrCreateNangoSessionToken } =
    useAuth();
  const showToast = useToast();
  // Shared, app-wide integrations list (read by ConnectionAlert and others).
  // HubspotContext keeps its own list for the settings UI, so we must refresh
  // this one too after connect/disconnect/reconnect — otherwise the dashboard
  // banner stays stale until a full page reload.
  const { fetchAllIntegrations: refreshGlobalIntegrations } = useIntegrations();

  // Connection and integration states
  const [loading, setLoading] = useState(false);
  const [connectLoading, setConnectLoading] = useState(false);
  const [disconnectLoading, setDisconnectLoading] = useState(false);
  const [reconnectLoading, setReconnectLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [integrations, setIntegrations] = useState([]);
  const [error, setError] = useState(null);
  const [enableSyncLoading, setEnableSyncLoading] = useState(false);
  const [disableForm, setDisableForm] = useState(false);

  // Field mapping and customization states
  const [pipelines, setPipelines] = useState([]);
  const [hsFields, setHsFields] = useState({});
  const [panoraFields, setPanoraFields] = useState([]);
  const [localMapping, setLocalMapping] = useState({});
  const [hubspotCustomizations, setHubspotCustomizations] = useState({});
  const [lastSyncInfo, setLastSyncInfo] = useState({
    at: "",
    status: "",
  });

  // Form states
  const [saveLoading, setSaveLoading] = useState(false);

  // Derived — single source of truth for the HubSpot integration record
  const hsIntegration = useMemo(
    () => integrations.find((i) => i.integration_id === "hubspot") ?? null,
    [integrations],
  );

  // Computed values.
  //
  // TWO signals, deliberately not one (PAN-258). `isConnected` used to mean both
  // "a record exists" and "the connection works", which is how a row stranded in
  // PENDING_SETUP for six days rendered a green dot, a Disconnect button and a
  // Resync button while syncing nothing.
  //
  //   hasIntegration   — a record exists. Gates the page body and the setup wizard,
  //                      which runs *during* PENDING_SETUP and must keep rendering.
  //   connectionHealth — backend-derived bucket. The preferred source for the status dot,
  //                      the banner text and Resync.
  //
  // Two things this is deliberately NOT, both learned by shipping the mistake:
  //
  //   It does not gate Connect-vs-Disconnect. That reads `connection_id`, because Disconnect
  //   is how a customer revokes our CRM access and a row in the `disconnected` bucket can
  //   still hold a live provider connection. Gating it on health made `destroy` unreachable
  //   from the UI. See HsStatusBar.
  //
  //   `undefined` (an API predating this field) does not mean "render nothing". It means
  //   "fall back to `status`, deliberately, per bucket". Treating it as silence let a
  //   stranded PENDING_SETUP row render green during a frontend-first deploy — the incident
  //   reproduced. Where a legacy source exists (`status`), use it; where none exists, and
  //   only there, stay quiet.
  const hasIntegration = !!hsIntegration;
  const connectionHealth = hsIntegration?.connection_health;
  const isHubspotFieldsSynced = hsIntegration?.hubspot_pipelines
    ? Array.isArray(hsIntegration.hubspot_pipelines)
      ? hsIntegration.hubspot_pipelines.length > 0
      : Object.keys(hsIntegration.hubspot_pipelines).length > 0
    : false;
  const initialHubspotCustomizations =
    hsIntegration?.hubspot_customizations ?? {};

  // Fetch all integrations
  const fetchIntegrations = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(TEAM_INTEGRATIONS_ENDPOINT);
      setIntegrations(response.data.results);
    } catch (err) {
      console.error("Failed to fetch integrations:", err);
      const errorMsg = "Failed to load integrations. Please try again.";
      setError(errorMsg);
      showToast({
        content: errorMsg,
        type: NOTIFICATION_TYPES.error,
      });
    } finally {
      setLoading(false);
    }
  };

  const formatMappingData = (data) => {
    const mapping = {};

    // Check if data is an array before calling forEach
    if (!Array.isArray(data)) {
      console.warn(
        "formatMappingData: Expected array but received:",
        typeof data,
        data,
      );
      return mapping; // Return empty mapping if data is not an array
    }

    data.forEach((item) => {
      if (item.mapping === "none") {
        mapping[item.panora_field] = item.mapping;
      } else {
        mapping[item.panora_field] = {
          model: item.model,
          field: item.field,
          pipelines: item.pipelines,
        };
      }
    });

    return mapping;
  };

  // Normalize customizations to always use consistent format
  const normalizeCustomizations = (customizations) => {
    if (!customizations) return {};

    const normalized = { ...customizations };

    // Keep active_deal_pipelines as array of objects
    if (normalized.active_deal_pipelines) {
      normalized.active_deal_pipelines = Array.isArray(
        normalized.active_deal_pipelines,
      )
        ? normalized.active_deal_pipelines
        : [];
    }

    // Keep pipeline fields as objects (don't normalize to IDs)
    // renewal_pipeline, churned_deal_pipeline, upsell_expansion_pipeline remain as objects

    // Keep stage arrays as arrays of objects (don't normalize to IDs)
    // renewal_deal_stages, churned_deal_stages, upsell_opportunity_stages remain as arrays of objects

    return normalized;
  };

  // Fetch field mapping data
  const fetchFieldMappingData = async () => {
    if (!hsIntegration?.id) return;

    setLoading(true);
    setError(null);

    try {
      // Fetch integration fields
      const integrationResponse = await apiClient.get(
        `/${TEAM_INTEGRATIONS_ENDPOINT}${hsIntegration.id}/`,
      );
      setLastSyncInfo({
        at: integrationResponse.data.last_sync,
        status: integrationResponse.data.sync_status,
      });
      setDisableForm(integrationResponse.data.status === "ACTIVE");
      setHsFields(integrationResponse.data.hubspot_custom_fields ?? {});
      setLocalMapping(
        formatMappingData(
          integrationResponse.data.panora_to_hubspot_mapping ?? [],
        ),
      );
      setPipelines(integrationResponse.data.hubspot_pipelines ?? []);
      setHubspotCustomizations(
        normalizeCustomizations(
          integrationResponse.data.hubspot_customizations ?? {},
        ),
      );

      // Fetch filtered fields
      const filteredFieldsResponse = await apiClient.get(
        "common/models/filtered_fields/",
      );
      setPanoraFields(filteredFieldsResponse.data);
    } catch (err) {
      console.error("Failed to fetch field mapping data:", err);
      const errorMsg =
        err.response?.data?.message ||
        "Failed to load field mapping data. Please try again.";
      setError(errorMsg);
      showToast({
        content: errorMsg,
        type: NOTIFICATION_TYPES.error,
      });
    } finally {
      setLoading(false);
    }
  };

  // Refetch all data
  const refetchData = async () => {
    await fetchIntegrations();
    // Keep the shared integrations list in sync so ConnectionAlert (and any
    // other consumer of IntegrationsContext) reflects the new status without
    // requiring a page reload.
    await refreshGlobalIntegrations();
    // fetchFieldMappingData is triggered by useEffect on hsIntegration?.id
  };

  // Sync HubSpot fields
  const syncHubspotFields = async () => {
    try {
      const response = await apiClient.post(`/integration/hubspot/sync/all/`);
      const { status: syncStatus, message } = response.data;

      // Backend returns status: "success" | "partial" | "error"
      const toastType =
        syncStatus === "success"
          ? NOTIFICATION_TYPES.success
          : NOTIFICATION_TYPES.error;

      showToast({
        content: message || "HubSpot fields have been synced successfully",
        type: toastType,
      });
      await refetchData();
    } catch (err) {
      console.error("Failed to sync HubSpot fields:", err);
      const errorMsg =
        err.response?.data?.message ||
        "Failed to sync HubSpot fields. Please try again.";
      showToast({
        content: errorMsg,
        type: NOTIFICATION_TYPES.error,
      });
      throw err;
    }
  };

  // Connect to HubSpot
  const handleHubSpotConnect = async () => {
    setConnectLoading(true);
    setError(null);
    try {
      const teamName = await getTeamName(apiClient);

      await connectIntegration({
        integrationId: "hubspot",
        authType: AUTH_TYPES.OAUTH,
        apiClient,
        teamId,
        userId,
        userEmail: user.email,
        showToast,
        getOrCreateNangoSessionToken,
        teamName,
        endpoint: TEAM_INTEGRATIONS_ENDPOINT,
        onSuccess: async () => {
          // Sync HubSpot fields BEFORE refetchData so that when the
          // useEffect on hsIntegration?.id fires fetchFieldMappingData,
          // pipeline data is already populated on the backend.
          setSyncLoading(true);
          try {
            // syncHubspotFields calls refetchData internally on success
            await syncHubspotFields();
          } catch (syncErr) {
            // Error already handled in syncHubspotFields.
            // Still need to refetch so the integration shows up.
            await refetchData();
          } finally {
            setSyncLoading(false);
          }
        },
        onError: (err) => {
          setError(
            err.message || "Failed to connect HubSpot. Please try again.",
          );
        },
      });
    } catch (err) {
      // Error already handled in connectIntegration
    } finally {
      setConnectLoading(false);
    }
  };

  // Disconnect from HubSpot
  const handleHubSpotDisconnect = async (integration) => {
    setDisconnectLoading(true);
    setError(null);
    try {
      await disconnectIntegration({
        integrationId: "hubspot",
        integration,
        apiClient,
        showToast,
        endpoint: TEAM_INTEGRATIONS_ENDPOINT,
        onSuccess: async () => {
          setIntegrations(integrations.filter((i) => i.id !== integration.id));
          // Clear all field mapping state to prevent stale data on reconnect
          setLocalMapping({});
          setHubspotCustomizations({});
          setHsFields({});
          setPipelines([]);
          setLastSyncInfo({ at: "", status: "" });
          setPanoraFields([]);
          // Keep the shared integrations list in sync (ConnectionAlert et al.)
          await refreshGlobalIntegrations();
        },
        onError: (err) => {
          setError(
            err.message || "Failed to disconnect HubSpot. Please try again.",
          );
        },
      });
    } catch (err) {
      // Error already handled in disconnectIntegration
    } finally {
      setDisconnectLoading(false);
    }
  };

  // Reconnect a stale/expired HubSpot integration
  const handleHubSpotReconnect = async () => {
    if (!hsIntegration?.id) return;

    setReconnectLoading(true);
    setError(null);
    try {
      // Step 1: Tell BE to delete stale Nango connection (preserves config)
      await apiClient.post(
        `${TEAM_INTEGRATIONS_ENDPOINT}${hsIntegration.id}/reconnect/`,
      );

      // Step 2: Re-authorize via Nango OAuth to get new connection
      const teamName = await getTeamName(apiClient);
      const nangoConnectSessionToken = await getOrCreateNangoSessionToken(
        userId,
        user.email,
      );

      const { default: Nango, AuthError } = await import("@nangohq/frontend");
      const nango = new Nango({
        connectSessionToken: nangoConnectSessionToken,
      });
      const nangoTeamId = `${teamName}-${teamId}`;

      let authResult;
      try {
        authResult = await nango.auth("hubspot");
      } catch (nangoErr) {
        // User closed OAuth popup — not an error
        if (
          nangoErr instanceof AuthError &&
          nangoErr.type === "WINDOW_CLOSED"
        ) {
          setReconnectLoading(false);
          return;
        }
        throw nangoErr;
      }

      // Step 3: PATCH the existing record with new connection_id and ACTIVE status
      await apiClient.patch(
        `${TEAM_INTEGRATIONS_ENDPOINT}${hsIntegration.id}/`,
        {
          connection_id: authResult.connectionId,
          name: `${userId}:${nangoTeamId}`,
          status: "ACTIVE",
        },
      );

      showToast({
        content: "HubSpot reconnected successfully!",
        type: NOTIFICATION_TYPES.success,
      });

      // Step 4: Sync fields and refresh field mapping data.
      //
      // ⚠️ PAN-525 — sync-all MUST run AFTER the PATCH resolves (line 385 above).
      // If it races, the sync fires against a stale/absent connection_id, silently
      // no-ops, and the cached HubSpot property list (`HubSpotIntegrationConfig.
      // custom_fields`) stays empty forever — new HubSpot properties never appear
      // in the mapping dropdown. Do not parallelize with the PATCH.
      //
      // The integration ID doesn't change on reconnect, so the useEffect on
      // hsIntegration?.id won't fire — we must call fetchFieldMappingData
      // explicitly to update disableForm, localMapping, and pipelines from the
      // backend.
      setSyncLoading(true);
      try {
        // syncHubspotFields calls refetchData internally on success
        await syncHubspotFields();
      } catch (syncErr) {
        // Non-fatal — config was preserved from before reconnect.
        // Still refetch so the UI reflects the new ACTIVE status.
        await refetchData();
      } finally {
        await fetchFieldMappingData();
        setSyncLoading(false);
      }
    } catch (err) {
      const errorMsg =
        err?.response?.data?.error ||
        err?.message ||
        "Failed to reconnect HubSpot. Please try again.";
      setError(errorMsg);
      showToast({
        content: errorMsg,
        type: NOTIFICATION_TYPES.error,
      });
    } finally {
      setReconnectLoading(false);
    }
  };

  // Check if integration needs reconnection. Reads the backend-derived bucket; the
  // status list is kept only as a fallback for an API that predates connection_health,
  // and must not grow — new statuses get a bucket on the backend, not a mirror here.
  const needsReconnection = connectionHealth
    ? connectionHealth === "needs_reconnect"
    : Boolean(
        hsIntegration?.status &&
          ["EXPIRED", "INVALID", "ERROR"].includes(hsIntegration.status),
      );

  // Update local customization state only — persisted via handleSubmit when
  // the user clicks "Save mappings" or "Save and enable sync".
  const updateHsCustomizations = (field) => {
    if (!hsIntegration?.id) {
      showToast({
        content: "No integration found. Please connect HubSpot first.",
        type: NOTIFICATION_TYPES.error,
      });
      return;
    }
    setHubspotCustomizations((prev) =>
      normalizeCustomizations({ ...prev, ...field }),
    );
  };

  // Submit field mappings
  const handleSubmit = async ({ mappingsOverride, ...args }) => {
    const mappings = mappingsOverride || localMapping;
    if (!hsIntegration?.id) {
      showToast({
        content: "No integration found. Please connect HubSpot first.",
        type: NOTIFICATION_TYPES.error,
      });
      return;
    }

    setSaveLoading(true);
    setError(null);

    try {
      const data = Object.entries(mappings).map(([panoraField, mapping]) => {
        if (mapping === "none") {
          return { panora_field: panoraField, mapping: "none" };
        }

        // Trying to be explicit about the return to avoid accidental bugs
        return {
          field: mapping.field,
          model: mapping.model,
          pipelines: mapping.pipelines,
          panora_field: panoraField,
        };
      });

      const res = await apiClient.patch(
        `${TEAM_INTEGRATIONS_ENDPOINT}${hsIntegration.id}/`,
        {
          panora_to_hubspot_mapping: data,
          hubspot_customizations: hubspotCustomizations,
          ...args,
        },
      );

      if (res.status === 200) {
        if (res.data.hubspot_customizations) {
          setHubspotCustomizations(
            normalizeCustomizations(res.data.hubspot_customizations),
          );
        }
        showToast({
          content: "HubSpot settings saved successfully",
          type: NOTIFICATION_TYPES.success,
        });
      } else {
        throw new Error("Failed to save HubSpot settings");
      }
    } catch (err) {
      console.error("Failed to save field mappings:", err);
      const errorMsg =
        err.response?.data?.message ||
        "Failed to save fields mapping. Please try again.";
      setError(errorMsg);
      showToast({
        content: errorMsg,
        type: NOTIFICATION_TYPES.error,
      });
      throw err;
    } finally {
      setSaveLoading(false);
    }
  };

  const handleEnableSync = async () => {
    setEnableSyncLoading(true);
    setError(null);
    try {
      // PATCH status to ACTIVE — locks the form and allows webhook processing.
      // Nango auto-starts syncs when the OAuth connection is created, so we
      // don't need to call sync/start explicitly.
      await handleSubmit({ status: "ACTIVE" });
      showToast({
        content: "Sync enabled successfully",
        type: NOTIFICATION_TYPES.success,
      });
      // Refresh BOTH: the integrations array (so hsIntegration.status and
      // connection_health update, which drives HsStatusBar) AND the single-
      // integration view (so disableForm, localMapping, pipelines update,
      // which drives HsSummary vs HsSetupStepper). Skipping refetchData left
      // the status bar showing "Setup not finished" after a successful save
      // because HsStatusBar reads hsIntegration.connection_health directly
      // (HsStatusBar.js:72-74) and fetchFieldMappingData does not touch the
      // integrations array.
      await refetchData();
      await fetchFieldMappingData();
    } catch (err) {
      console.error("Failed to enable sync:", err);
      const errorMsg =
        err.response?.data?.message ||
        "Failed to enable sync. Please try again.";
      setError(errorMsg);
      showToast({
        content: errorMsg,
        type: NOTIFICATION_TYPES.error,
      });
    } finally {
      setEnableSyncLoading(false);
    }
  };

  // Effects
  useEffect(() => {
    if (apiClient) {
      fetchIntegrations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiClient]);

  useEffect(() => {
    if (hsIntegration?.id) {
      fetchFieldMappingData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hsIntegration?.id]);

  const value = {
    // Connection states
    loading,
    connectLoading,
    disconnectLoading,
    reconnectLoading,
    syncLoading,
    hasIntegration,
    connectionHealth,
    needsReconnection,
    error,

    // Integration data
    integrations,
    hsIntegration,
    isHubspotFieldsSynced,

    // Field mapping data
    pipelines,
    hsFields,
    panoraFields,
    localMapping,
    setLocalMapping,
    lastSyncInfo,

    // Customizations
    hubspotCustomizations,
    initialHubspotCustomizations,

    // Form states
    saveLoading,
    enableSyncLoading,
    disableForm,

    // Actions
    handleHubSpotConnect,
    handleHubSpotDisconnect,
    handleHubSpotReconnect,
    updateHsCustomizations,
    handleSubmit,
    refetchData,
    handleEnableSync,
    syncHubspotFields,
  };

  return (
    <HubspotContext.Provider value={value}>{children}</HubspotContext.Provider>
  );
};
